import React from 'react';
import { Shield, CheckCircle, Clock, FileText, Award, ArrowRight, Zap, Users, Lock, TrendingUp, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import PopupButton from '../components/PopupButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import TestimonialSlider from '../components/TestimonialSlider';
import HowItWorksSlider from '../components/HowItWorksSlider';
import WhyYouNeedItSlider from '../components/WhyYouNeedItSlider';
import WhyChooseUsSlider from '../components/WhyChooseUsSlider';
import BenefitsSlider from '../components/BenefitsSlider';
import { pricingPlans, trademarkTestimonials, faqs } from '../data/mock';
import SEO from '../components/SEO';

const TrademarkRegistration = () => {
  const processSteps = [
    {
      step: 1,
      title: 'Submit Your Details',
      description: 'Fill out a quick form with your brand name and business information.',
      icon: FileText
    },
    {
      step: 2,
      title: 'We Handle the Filing',
      description: 'Our experts prepare and file your trademark application with the USPTO.',
      icon: Shield
    },
    {
      step: 3,
      title: 'Get Official Protection',
      description: 'Once approved, your trademark is officially registered and protected.',
      icon: Award
    }
  ];

  const whyYouNeedIt = [
    {
      icon: Lock,
      title: 'Legal Protection',
      description: 'Secure exclusive rights to your brand name, logo, and identity nationwide.'
    },
    {
      icon: Shield,
      title: 'Prevent Infringement',
      description: 'Stop competitors from using similar marks that could confuse customers.'
    },
    {
      icon: TrendingUp,
      title: 'Increase Brand Value',
      description: 'A registered trademark adds significant value to your business assets.'
    },
    {
      icon: Award,
      title: 'Build Trust',
      description: 'Show customers your brand is legitimate and professionally protected.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: 'Fast Processing',
      description: 'We process applications within 24 hours'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Experienced trademark specialists handle your case'
    },
    {
      icon: CheckCircle,
      title: '100% Satisfaction',
      description: 'Guaranteed quality service and support'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'We\'re always here to help you'
    }
  ];

  const benefits = [
    {
      title: 'Nationwide Protection',
      description: 'Your trademark is protected across all 50 states automatically.'
    },
    {
      title: 'Legal Recourse',
      description: 'Sue infringers and recover damages if someone copies your brand.'
    },
    {
      title: 'Use ® Symbol',
      description: 'Display the registered trademark symbol to deter copycats.'
    },
    {
      title: 'Asset Value',
      description: 'Registered trademarks can be sold, licensed, or used as collateral.'
    },
    {
      title: 'Priority Rights',
      description: 'Establish your filing date and gain priority over later applicants.'
    },
    {
      title: 'Global Expansion',
      description: 'U.S. registration makes international trademark filing easier.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Trademark Registration Services | Assess Trademark"
        description="Secure your brand with professional trademark registration services. Assess Trademark helps businesses protect names, logos, and intellectual property efficiently."
      />
      {/* Banner */}
      <section className="py-12 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">Trademark Registration</h1>
        </div>
      </section>

      {/* 1. About the Service */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Trademark Registration
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-6">
                Secure your brand identity with fast, reliable trademark filing in the U.S. Our expert team handles the entire registration process, ensuring your brand gets the legal protection it deserves.
              </p>
              <p className="text-base md:text-lg text-gray-600 mb-8">
                Starting at just $45, we offer affordable trademark registration services with 100% satisfaction guarantee. Let us help you protect what you've built.
              </p>
              <PopupButton size="lg" className="bg-secondary hover:bg-secondary/90 text-white">
                Get Started Now
              </PopupButton>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-xl flex items-center justify-center bg-blue-50 p-4">
                <img 
                  src="/assets/img/trademark-registration.svg" 
                  alt="Trademark Registration Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Simple, fast, and transparent process</p>
          </div>
          <HowItWorksSlider items={processSteps} />
        </div>
      </section>

      {/* 3. Why You Need It */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why You Need Trademark Registration</h2>
            <p className="text-lg text-gray-600">Protect your brand and secure your business future</p>
          </div>
          <WhyYouNeedItSlider items={whyYouNeedIt} />
        </div>
      </section>

      {/* 4. Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Pricing Plans</h2>
            <p className="text-lg text-gray-600">Choose the package that fits your needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative border-2 flex flex-col h-full ${
                  plan.recommended 
                    ? 'border-primary shadow-2xl scale-105' 
                    : 'border-gray-200 hover:border-primary/60'
                } transition-all duration-300 hover:shadow-xl`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-secondary text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6 pt-8">
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-primary">${plan.price}</span>
                    <span className="text-gray-600 ml-2">+ Govt. Fee</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-4">{plan.description}</p>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton 
                    className={`w-full ${
                      plan.recommended
                        ? 'bg-secondary hover:bg-secondary/90'
                        : 'bg-gray-800 hover:bg-gray-900'
                    } text-white`}
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <p className="text-lg text-gray-600">We make trademark registration simple and stress-free</p>
          </div>
          <WhyChooseUsSlider items={whyChooseUs} />
        </div>
      </section>

      {/* 6. Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600">What you get with trademark registration</p>
          </div>
          <BenefitsSlider benefits={benefits} />
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600">Real experiences from real clients</p>
          </div>
          <TestimonialSlider testimonials={trademarkTestimonials} />
        </div>
      </section>

      {/* 8. FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about trademark registration</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border-b border-gray-200">
                <AccordionTrigger className="text-left hover:text-primary transition-colors">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of businesses who have secured their trademarks with us. Get started today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PopupButton size="lg" className="bg-white text-primary hover:bg-gray-100">
              Start Your Application
              <ArrowRight className="ml-2 w-5 h-5" />
            </PopupButton>
            <Link to="/contact-us">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrademarkRegistration;